import React, { useState, useEffect } from 'react';
import { useClientAuth } from '../../context/ClientAuthContext';
import { clientApi, formatApiError } from '../../context/ClientAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { FileText, Bank, Signature, CheckCircle, Clock, Upload } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function ClientDashboard() {
  const { client } = useClientAuth();
  const [documents, setDocuments] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [docsRes, statusRes] = await Promise.all([
        clientApi.get('/documents'),
        clientApi.get('/onboarding-status'),
      ]);
      setDocuments(docsRes.data);
      setOnboardingStatus(statusRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const invoiceCount = documents?.invoices?.length || 0;
  const statementCount = documents?.bank_statements?.length || 0;

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <div>
        <h1 className="heading-2 mb-2">Welcome, {client?.name}</h1>
        <p className="text-muted-foreground">Manage your documents and view your account status</p>
      </div>

      {/* Onboarding Status */}
      {onboardingStatus && onboardingStatus.status !== 'onboarded' && (
        <Card className="border-2 border-yellow-300 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-full bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="heading-4 mb-2">Complete Your Onboarding</h3>
                <p className="text-muted-foreground mb-4">
                  Please review and sign the document provided by your accountant to complete your onboarding process.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    {onboardingStatus.has_loe ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-500" />
                    )}
                    <span className="text-sm">Document Received</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {onboardingStatus.has_signature ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-500" />
                    )}
                    <span className="text-sm">Your Signature</span>
                  </div>
                </div>
                {onboardingStatus.has_loe && !onboardingStatus.has_signature && (
                  <Link
                    to="/portal/signature"
                    className="inline-block mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90"
                    data-testid="sign-loe-link"
                  >
                    Sign Now
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Card */}
      {onboardingStatus?.status === 'onboarded' && (
        <Card className="border border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="heading-4 text-green-800">Onboarding Complete</h3>
                <p className="text-green-700 text-sm">
                  Your account is fully set up. You can now upload documents.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline mb-2">Invoices Uploaded</p>
                <p className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {invoiceCount}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Purchase & sales invoices</p>
              </div>
              <div className="p-3 rounded-md bg-blue-50">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline mb-2">Bank Statements</p>
                <p className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {statementCount}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Statements uploaded</p>
              </div>
              <div className="p-3 rounded-md bg-purple-50">
                <Bank className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline mb-2">Account Status</p>
                <Badge
                  variant="secondary"
                  className={onboardingStatus?.status === 'onboarded' ? 'status-onboarded' : 'status-pending'}
                >
                  {onboardingStatus?.status || 'Pending'}
                </Badge>
                <p className="text-sm text-muted-foreground mt-2">
                  {onboardingStatus?.status === 'onboarded' ? 'Fully onboarded' : 'Pending signature'}
                </p>
              </div>
              <div className="p-3 rounded-md bg-green-50">
                <Signature className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border border-border">
        <CardHeader className="pb-4">
          <CardTitle className="heading-4">Quick Actions</CardTitle>
          <CardDescription>Upload your documents for your accountant to review</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/portal/upload"
              className="p-4 rounded-md border border-border hover:bg-muted transition-colors text-center"
              data-testid="quick-upload-invoice"
            >
              <Upload className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Upload Invoice</p>
            </Link>
            <Link
              to="/portal/upload"
              className="p-4 rounded-md border border-border hover:bg-muted transition-colors text-center"
              data-testid="quick-upload-statement"
            >
              <Bank className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Upload Bank Statement</p>
            </Link>
            <Link
              to="/portal/documents"
              className="p-4 rounded-md border border-border hover:bg-muted transition-colors text-center"
              data-testid="quick-view-docs"
            >
              <FileText className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">View All Documents</p>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
